import { basketball, cricketBall, flowerLotus, golfDriver, soccerBall, tennisBall, tennisRacket } from '@lucide/lab';
import { publicStorageUrl, toStorageRef } from '@kit/shared/storage';
import { Activity, Bike, CircleDot, Dumbbell, Flame, Footprints, HandFist, HeartPulse, Mountain, PersonStanding, Salad, Stethoscope, Target, Trophy, Volleyball, Waves, createLucideIcon } from 'lucide-react';

export const SPORT_ICONS = {
  'tennis-racket': createLucideIcon('tennis-racket', tennisRacket),
  'tennis-ball': createLucideIcon('tennis-ball', tennisBall),
  'soccer-ball': createLucideIcon('soccer-ball', soccerBall),
  basketball: createLucideIcon('basketball', basketball),
  volleyball: Volleyball,
  'flower-lotus': createLucideIcon('flower-lotus', flowerLotus),
  'golf-driver': createLucideIcon('golf-driver', golfDriver),
  'cricket-ball': createLucideIcon('cricket-ball', cricketBall),
  'hand-fist': HandFist,
  stethoscope: Stethoscope,
  salad: Salad,
  dumbbell: Dumbbell,
  activity: Activity,
  bike: Bike,
  'circle-dot': CircleDot,
  flame: Flame,
  footprints: Footprints,
  'heart-pulse': HeartPulse,
  mountain: Mountain,
  'person-standing': PersonStanding,
  target: Target,
  trophy: Trophy,
  waves: Waves,
};

export type SportIconKey = keyof typeof SPORT_ICONS;

export function isSportIconKey(icon?: string | null): icon is SportIconKey {
  return !!icon && Object.hasOwn(SPORT_ICONS, icon);
}

// Most tenants never pick an icon and name sports freely (hot-yoga,
// reformer-pilates), so slug words map to a glyph when nothing is set.
const SLUG_ICON_KEYWORDS: Array<[keywords: string[], icon: string]> = [
  [['football', 'soccer', 'futsal'], 'soccer-ball'],
  [['basketball'], 'basketball'],
  [['volleyball'], 'volleyball'],
  [['tennis'], 'tennis-racket'],
  [['badminton', 'squash', 'pickleball'], 'tennis-racket'],
  [['yoga'], 'flower-lotus'],
  [['pilates', 'reformer', 'lagree', 'fitness', 'gym', 'crossfit', 'strength'], 'dumbbell'],
  [['golf'], 'golf-driver'],
  [['cricket'], 'cricket-ball'],
  [['boxing', 'kickboxing', 'mma', 'karate', 'taekwondo', 'jiujitsu'], 'hand-fist'],
  [['running', 'run', 'athletics'], 'footprints'],
  [['swimming', 'swim', 'aqua', 'pool'], 'waves'],
  [['cycling', 'spin', 'bike'], 'bike'],
  [['climbing', 'bouldering'], 'mountain'],
  [['physiotherapy', 'physio'], 'stethoscope'],
  [['nutrition'], 'salad'],
];

export function sportIconKeyForSlug(slug: string): SportIconKey | null {
  const words = slug.toLowerCase().split(/[-_\s]+/);
  for (const [keywords, icon] of SLUG_ICON_KEYWORDS) {
    if (isSportIconKey(icon) && keywords.some((keyword) => words.includes(keyword))) {
      return icon;
    }
  }
  return null;
}


export type SportVisual =
  | { kind: 'image'; src: string }
  | { kind: 'glyph'; key: SportIconKey }
  | { kind: 'emoji'; value: string };

/** The storage reference of an uploaded sport image held in `sports.icon`; null for glyphs, emoji or unset. */
export function sportImageRef(icon?: string | null): string | null {
  return icon && !isSportIconKey(icon) ? toStorageRef(icon) : null;
}

function readSportVisual(icon: string | null | undefined): SportVisual | null {
  const value = icon?.trim();
  if (!value) return null;
  if (isSportIconKey(value)) return { kind: 'glyph', key: value };
  // Uploads are storage references; legacy rows may hold a URL, a bundled
  // `/images/...` path or a bare emoji. Unknown slug-like keys are ignored.
  if (sportImageRef(value) || /^(https?:\/\/|\/(?!\/))/.test(value)) {
    return { kind: 'image', src: publicStorageUrl(value) };
  }
  return /^[\w-]+$/.test(value) ? null : { kind: 'emoji', value };
}

/**
 * What every customer picker shows for a sport: the first chosen value that
 * resolves (the sport's own `icon` from Sports management first, then legacy
 * overrides), else a glyph matched from the slug.
 */
export function resolveSportVisual(slug: string, ...chosen: Array<string | null | undefined>): SportVisual | null {
  for (const icon of chosen) {
    const visual = readSportVisual(icon);
    if (visual) return visual;
  }
  const key = sportIconKeyForSlug(slug);
  return key ? { kind: 'glyph', key } : null;
}

/**
 * The app home Sports strip: the sport's own picture, except that an image
 * uploaded in the home builder before pictures moved to Sports management
 * still beats a plain glyph.
 */
export function resolveHomeSportVisual(
  slug: string,
  sportIcon: string | null | undefined,
  legacyImageUrl: string,
  legacyIcon: string | undefined,
): SportVisual {
  const own = readSportVisual(sportIcon);
  const ordered = own?.kind === 'glyph' || !own
    ? [legacyImageUrl, sportIcon, legacyIcon]
    : [sportIcon, legacyImageUrl, legacyIcon];
  return resolveSportVisual(slug, ...ordered) ?? { kind: 'glyph', key: 'dumbbell' };
}
